/** A run of text, marked when it is part of the search match. */
export interface HighlightPart {
  text: string;
  match: boolean;
}

/**
 * Split `text` into runs so every query term's first occurrence can be
 * emphasised. Terms that only match as a scattered subsequence (the ranking
 * allows that) are not highlighted: marking single letters reads as noise.
 */
export function highlightParts(text: string, query: string): HighlightPart[] {
  const lower = text.toLowerCase();
  const ranges = query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => [lower.indexOf(term), term.length] as const)
    .filter(([start]) => start >= 0)
    .map(([start, length]) => [start, start + length] as const)
    .sort((a, b) => a[0] - b[0]);

  const parts: HighlightPart[] = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (end <= cursor) continue;
    const from = Math.max(start, cursor);
    if (from > cursor)
      parts.push({ text: text.slice(cursor, from), match: false });
    parts.push({ text: text.slice(from, end), match: true });
    cursor = end;
  }
  if (cursor < text.length)
    parts.push({ text: text.slice(cursor), match: false });
  return parts;
}

/** Whether every term of the query appears literally in `text`. */
export function containsAllTerms(text: string, query: string): boolean {
  const lower = text.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => lower.includes(term));
}
